/**
 * Auto-update plumbing for the installed app.
 *
 * vite-plugin-pwa registers the service worker (injectRegister: 'auto') and
 * activates each new version the moment it is downloaded
 * (registerType: 'autoUpdate') — but a page that is *already open* keeps
 * running the old bundle until something triggers a reload. This module
 * closes that gap:
 *
 *   - reloads as soon as a new service worker takes control,
 *   - never yanks the page away during playback (the player surfaces an
 *     "update ready" toast instead and reloads when the user asks),
 *   - re-checks for a new version whenever the tab returns to the foreground
 *     and every 30 minutes, so a session left open for hours still picks up
 *     the next release without the user doing anything.
 */

export type AppUpdateEvent = 'update-ready'

type Listener = (event: AppUpdateEvent) => void

const listeners = new Set<Listener>()

/** Subscribe to app update events. Returns an unsubscribe function. */
export function onAppUpdate(listener: Listener): () => void {
  listeners.add(listener)
  return () => {
    listeners.delete(listener)
  }
}

function emit(event: AppUpdateEvent) {
  listeners.forEach((listener) => listener(event))
}

/** Apply a waiting update right away (used by the toast button). */
export function applyUpdate() {
  window.location.reload()
}

/** Ask the browser to look for a newer service worker right now. */
export function checkForUpdates(): Promise<void> {
  if (!('serviceWorker' in navigator)) return Promise.resolve()
  return navigator.serviceWorker
    .getRegistration()
    .then((registration) => registration?.update())
    .then(() => undefined)
    .catch(() => undefined)
}

function isPlaybackRoute() {
  return window.location.pathname.startsWith('/player')
}

export function initAppUpdate() {
  if (typeof navigator === 'undefined' || !('serviceWorker' in navigator)) return
  // The Capacitor shell serves the app from the capacitor:// scheme, where
  // WebKit refuses to register a service worker — nothing to update there
  // (native builds ship through the App Store instead).
  if (window.location.protocol === 'capacitor:') return

  // The very first install claims the page too — that controllerchange is not
  // an update, so ignore it or every first-time visitor would reload once.
  let hadController = Boolean(navigator.serviceWorker.controller)

  navigator.serviceWorker.addEventListener('controllerchange', () => {
    if (!hadController) {
      hadController = true
      return
    }
    if (isPlaybackRoute()) {
      emit('update-ready')
      return
    }
    window.location.reload()
  })

  const refresh = () => {
    void checkForUpdates()
  }

  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') refresh()
  })
  window.setInterval(refresh, 30 * 60 * 1000)
}
